import { getPeriodByEndDate } from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import 'react-table-6/react-table.css';
import { CourierTravelModeString, TravelMode } from 'types/bundleTypes';
import {
  avgDeviationCompPercentage,
  bike_Per_Delivery_Price,
  calcBonusCompensationBasedOnTravelMode,
  calcCompensationForShiftBasedOnTravelMode,
  calcVatForCouriers,
  car_Per_Delivery_Price,
  compensationModelStavanger,
  minRatingForBonus,
} from 'utils/costConstants';
import { ReqEnum } from '../../../const/requestStatus';
import {
  ExtraWork,
  ExtraWorkConst,
  ShiftCourierJson,
  ShiftCourierWithDateJson,
} from '../../../const/ShiftConst';
import { SummaryInvoiceData } from '../../../pages/myDeliveries/InvoiceCourierCompanyReportComposition';
import '../../../styles/4-components/_react-table.scss';
import { Region, reqStatus } from '../../../types/orderTypes';
import { CourierInvoice } from '../../../types/reportTypes';
import {
  fetchIsMvaRegistered,
  getOrgNumber,
} from '../../../utils/enhetsregisteretUtil';
import { Loader } from '../../Loader/Loader';
import '../OrderListTable.scss';
import {
  ExtraWorkCompensation,
  PerDayInvoice,
} from '../utils/arrayManipulation2';
import {
  CalcExtraWorkCompensation,
  EmptyExtraWorkCompensation,
} from '../utils/calcCostPerDayInvoice';
import { prodCostTableForCourierWithShiftData } from '../utils/prodCostTableForCourierWithShiftData';
import { roundToOne, roundToTwo } from '../utils/roundToTwo';
import './BillingCourier.scss';
import './BillingCourierTable.scss';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';

function useOutsideOsloCompensationModel(region): boolean {
  return [Region.STAVANGER, Region.TRONDHEIM, Region.BERGEN].includes(region);
}

export function InvoiceHeader({
  startDate,
  endDate,
  titleText = 'Courier period report',
}: {
  startDate: Dayjs;
  endDate: Dayjs;
  titleText?: string;
}): JSX.Element {
  return (
    <div className="invoiceHeader">
      <div className="SummaryInvoiceTitle">{titleText}</div>
      <div className="sText date">
        Created: {dayjs(new Date()).format('YY MMM DD')}
      </div>
      <div className="sText period">Period {getPeriodByEndDate(endDate)}</div>
      <div className="sText range">
        {` Dates:  ${startDate.format('MMM DD')} - ${endDate.format('MMM DD')}`}
      </div>
    </div>
  );
}

function travelModeCompensation(
  travelMode: TravelMode,
  useOutsideOsloCompensationModel = false
) {
  if (useOutsideOsloCompensationModel) {
    if (travelMode === TravelMode.NONE) {
      return 0;
    } else if (travelMode === TravelMode.CAR) {
      return compensationModelStavanger.carPerDeliveryPrice;
    } else if (travelMode === TravelMode.BIKE) {
      return compensationModelStavanger.bikePerDeliveryPrice;
    }
  }
  if (travelMode === TravelMode.NONE) {
    return 0;
  } else if (travelMode === TravelMode.CAR) {
    return car_Per_Delivery_Price;
  } else if (travelMode === TravelMode.BIKE) {
    return bike_Per_Delivery_Price;
  }
}

export function calcSumDeviationAvgCompensation(
  totalNrOforders: number,
  courierTravelMode: TravelMode
): number {
  const sumDeviationAvgCompensation =
    totalNrOforders *
    avgDeviationCompPercentage *
    travelModeCompensation(courierTravelMode);
  return sumDeviationAvgCompensation;
}

async function checkIsRegisteredAsMVA(orgNummer) {
  if (!isNaN(orgNummer)) {
    const isRegistered = await fetchIsMvaRegistered(orgNummer.toString());
    return isRegistered;
  } else {
    return false;
  }
}

export function useFetchIsMva(orgNummer): {
  isMva: boolean;
  fetchIsMvaStatus: reqStatus;
  vatForCouriers: number;
} {
  const [fetchIsMvaStatus, setFetchisMvaStatus] = useState<reqStatus>(
    ReqEnum.INIT
  );
  const [isMva, setIsMva] = useState<boolean>();
  const [vatForCouriers, setVatForCouriers] = useState<number>(0);

  useEffect(() => {
    async function fetchMva(orgNummer) {
      setFetchisMvaStatus(ReqEnum.PENDING);
      const mvaResult: boolean = await checkIsRegisteredAsMVA(orgNummer);
      const vatForCouriersValue: number = calcVatForCouriers(mvaResult);
      setVatForCouriers(vatForCouriersValue);
      setIsMva(mvaResult);
      setFetchisMvaStatus(ReqEnum.SUCCESS);
    }

    fetchMva(orgNummer);
  }, [orgNummer]);

  return { isMva, fetchIsMvaStatus, vatForCouriers };
}

export function NumberAsInvoiceString(numberToFormat: number): string {
  if (numberToFormat) {
    const formatedNumber = String(numberToFormat.toFixed(2))
      .replace('.', ',')
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    // .replace(',00', '');

    const withKr = `${formatedNumber} kr`;
    return withKr;
  }
  return '0 kr';
}

export function getInvoiceSummaryData(
  courierInvoiceForSelectedCourier: CourierInvoice
): { courierName: string; orgNummer: string } {
  const courierName = `${courierInvoiceForSelectedCourier.courierFirstName}  ${courierInvoiceForSelectedCourier.courierLastName}`;

  const orgNummer: string = getOrgNumber(
    courierInvoiceForSelectedCourier.courierCompanyNumber
  );

  return { courierName, orgNummer };
}

export function calcInvoiceSummary(
  ordersPerDay: PerDayInvoice[],
  useOutsideOsloCompensationModel: boolean
): SummaryInvoiceData {
  if (ordersPerDay) {
    let normalOrders: PerDayInvoice[] = []; //Order which will have higher value in total than
    // minWage
    // in Stavanger
    const orderWithMinWage: PerDayInvoice[] = [];
    let minWageDays: number;
    let minWageSummary: number;
    if (useOutsideOsloCompensationModel) {
      ordersPerDay.forEach(order =>
        order.totalCompensation < 1400 && order.totalCompensation > 0
          ? orderWithMinWage.push(order)
          : normalOrders.push(order)
      );
      minWageDays = orderWithMinWage.length;
      minWageSummary =
        orderWithMinWage.length *
        compensationModelStavanger.minTotalCompForShift;
    } else {
      normalOrders = ordersPerDay;
    }
    const totalNrOforders: number =
      ordersPerDay.length > 0
        ? ordersPerDay
            .map(obj => obj.nrOfOrders)
            .reduce((accumulator, currentValue) => accumulator + currentValue)
        : 0;

    const sumCompensationForShifts: number =
      normalOrders.length > 0 &&
      normalOrders
        .map(obj => obj.compensationForShift)
        .reduce((accumulator, currentValue) => accumulator + currentValue);

    const SumCompensation: number =
      normalOrders.length > 0
        ? normalOrders
            .map(obj => obj.totalCompensation)
            .reduce((accumulator, currentValue) => accumulator + currentValue)
        : 0;

    const extraWorkCompensationList: ExtraWorkCompensation[] =
      normalOrders.length > 0
        ? normalOrders
            .map(objHere => objHere.shift)
            .filter(obj => obj !== undefined)
            .map((obj: ShiftCourierJson) => {
              return CalcExtraWorkCompensation(obj);
            })
        : [];

    const extraWorkCompensation: ExtraWorkCompensation = {
      allExtraWorkCompensation:
        extraWorkCompensationList.length > 0
          ? extraWorkCompensationList
              .map(obj => obj.allExtraWorkCompensation)
              .reduce((acc, cur) => acc + cur)
          : 0,
      sortWorkCompensation:
        extraWorkCompensationList.length > 0
          ? extraWorkCompensationList
              .map(obj => obj.sortWorkCompensation)
              .reduce((acc, cur) => acc + cur)
          : 0,
      inboundWorkCompensation:
        extraWorkCompensationList.length > 0
          ? extraWorkCompensationList
              .map(obj => obj.inboundWorkCompensation)
              .reduce((acc, cur) => acc + cur)
          : 0,
      backupWorkCompensation:
        extraWorkCompensationList.length > 0
          ? extraWorkCompensationList
              .map(obj => obj.backupWorkCompensation)
              .reduce((acc, cur) => acc + cur)
          : 0,
      otherWorkCompensation:
        extraWorkCompensationList.length > 0
          ? extraWorkCompensationList
              .map(obj => obj.otherWorkCompensation)
              .reduce((acc, cur) => acc + cur)
          : 0,
    };

    const nrOfDayShifts: number = normalOrders.filter(
      obj => obj.nrOfOrders > 0
    ).length;

    const compForDeliveries: number =
      normalOrders.length > 0 &&
      normalOrders
        .map(obj => obj.compensationForNormalDeliveries)
        .reduce((accumulator, currentValue) => accumulator + currentValue);

    return {
      totalNrOforders,
      sumCompensationForShifts,
      SumCompensation,
      nrOfDayShifts,
      compForDeliveries,
      extraWorkCompensation,
      minWageDays,
      minWageSummary,
    };
  } else {
    const totalNrOforders = 0;
    const sumCompensationForShifts = 0;
    const SumCompensation = 0;
    const nrOfDayShifts = 0;
    const compForDeliveries = 0;
    const extraWorkCompensation: ExtraWorkCompensation =
      EmptyExtraWorkCompensation;

    return {
      totalNrOforders,
      sumCompensationForShifts,
      SumCompensation,
      nrOfDayShifts,
      compForDeliveries,
      extraWorkCompensation,
    };
  }
}

export function CompanyInfoInvoiceBox({
  courierInvoiceForSelectedCourier,
  courierName,
  orgNummer,
  fetchIsMvaStatus,
  isMva,
}: {
  courierInvoiceForSelectedCourier: CourierInvoice;
  courierName: string;
  orgNummer: string;
  fetchIsMvaStatus: reqStatus;
  isMva: boolean;
}): JSX.Element {
  return (
    <div className="positionBox companyInfoInvoiceBox">
      <div className="textBox">
        <div className="title"> Company</div>
        <div className="bigTextBlock">
          {courierInvoiceForSelectedCourier.courierCompanyName
            ? courierInvoiceForSelectedCourier.courierCompanyName
            : 'Not registered'}
        </div>
        <div className="sText">
          <div>Courier:</div> <div>{courierName}</div>
        </div>

        <div className="sText">
          <div>Org. number:</div>
          <div>{orgNummer}</div>
        </div>

        <div className="sText">
          <div>VAT registered </div>
          <div>
            {fetchIsMvaStatus !== ReqEnum.SUCCESS ? (
              <Loader />
            ) : isMva ? (
              'Yes'
            ) : (
              'No'
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PeriodCompensationInfoBox({
  nrOfDayShifts,
  sumCompensationForShifts,
  totalNrOforders,
  compForDeliveries,
  SumCompensation,
  vatForCouriers,
  deviationComp,
  extraWorkCompensation,
  courierAvgRatingForDateRange,
  region,
  minWageSummary,
  minWageDays,
}: {
  nrOfDayShifts: number;
  sumCompensationForShifts: number;
  totalNrOforders: number;
  compForDeliveries: number;
  SumCompensation: number;
  vatForCouriers: number;
  deviationComp: number;
  extraWorkCompensation: ExtraWorkCompensation;
  courierAvgRatingForDateRange?: number;
  region: Region;
  minWageSummary: number;
  minWageDays: number;
}): JSX.Element {
  const isOutsideOsloSelected = useOutsideOsloCompensationModel(region);
  const totalSum = isOutsideOsloSelected
    ? minWageSummary + SumCompensation
    : SumCompensation;
  return (
    <div className="positionBox periodCompensationInfoBox">
      <div className="textBox">
        <div className="title"> Period Compensation</div>
        {minWageDays > 0 ? (
          <div className="compensationText">
            Min comp shifts:
            <div className="nrOfTimes"> {`${minWageDays} `} </div>
            <div className="sumFromNrOfTimes">
              {NumberAsInvoiceString(minWageSummary)}
            </div>
          </div>
        ) : null}
        <div className="compensationText">
          {minWageDays > 0 ? 'Normal shifts:' : 'Day shifts:'}
          <div className="nrOfTimes"> {`${nrOfDayShifts}`} </div>
          <div className="sumFromNrOfTimes">
            {NumberAsInvoiceString(sumCompensationForShifts)}
          </div>
        </div>
        <div className="compensationText">
          Deliveries:
          <div className="nrOfTimes"> {`${totalNrOforders} `} </div>
          <div className="sumFromNrOfTimes">
            {NumberAsInvoiceString(compForDeliveries)}
          </div>
        </div>
        {isOutsideOsloSelected ? null : (
          <div className="compensationText">
            Deviations:
            <div className="nrOfTimes">
              {`${Number(avgDeviationCompPercentage * 100)
                .toFixed(2)
                .replace('.', ',')}
              %`}
            </div>
            <div className="sumFromNrOfTimes">
              {NumberAsInvoiceString(deviationComp)}
            </div>
          </div>
        )}
        <div className="compensationText">
          Sorting comp:
          <div className="nrOfTimes">
            {`${Number(
              extraWorkCompensation.sortWorkCompensation /
                ExtraWorkConst.SORT.payment
            ).toFixed(0)}`}
          </div>
          <div className="sumFromNrOfTimes">
            {NumberAsInvoiceString(extraWorkCompensation.sortWorkCompensation)}
          </div>
        </div>
        <div className="compensationText">
          Other comp:
          <div className="sumFromNrOfTimes">
            {NumberAsInvoiceString(
              extraWorkCompensation.allExtraWorkCompensation -
                extraWorkCompensation.sortWorkCompensation
            )}
          </div>
        </div>
        {courierAvgRatingForDateRange > 0 && (
          <div className="compensationText">
            Avg Rating:
            <div className="sumFromNrOfTimes">
              {roundToOne(courierAvgRatingForDateRange)}
            </div>
          </div>
        )}
        <div className="bigTextBlock">
          <div>Total excl. VAT:</div>
          <div>
            {totalSum === undefined ? (
              <Loader />
            ) : (
              NumberAsInvoiceString(totalSum)
            )}
          </div>
        </div>
        <div className="bigTextBlock">
          <div>{`VAT(${vatForCouriers * 100}%):`}</div>
          <div>
            {totalSum === undefined ? (
              <Loader />
            ) : (
              NumberAsInvoiceString(totalSum * vatForCouriers)
            )}
          </div>
        </div>
        <div className="bigTextBlock">
          <div>Total incl. VAT:</div>
          <div>
            {totalSum === undefined ? (
              <Loader />
            ) : (
              NumberAsInvoiceString(totalSum * (1 + vatForCouriers))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompensationInfoBox({
  courierInvoiceForSelectedCourier,
  region,
}: {
  courierInvoiceForSelectedCourier: CourierInvoice;
  region: Region;
}): JSX.Element {
  const isOutsideOsloSelected = useOutsideOsloCompensationModel(region);
  return (
    <div className="positionBox compensationInfoBox">
      <div className="textBox">
        <div className="title"> Compensation variables</div>
        <div className="sText">
          <div>Type of delivery:</div>
          <div>{courierInvoiceForSelectedCourier.courierTravelMode}</div>
        </div>
        <div className="sText">
          <div> Per shift :</div>
          <div>
            {NumberAsInvoiceString(
              calcCompensationForShiftBasedOnTravelMode(
                courierInvoiceForSelectedCourier.courierTravelMode
              )
            )}
          </div>
        </div>

        <div className="sText">
          <div> Per delivery :</div>
          <div>
            {NumberAsInvoiceString(
              travelModeCompensation(
                courierInvoiceForSelectedCourier.courierTravelMode,
                isOutsideOsloSelected
              )
            )}
          </div>
        </div>
        <div className="sText">
          <div>{`Min rating for bonus`}</div>
          <div>{minRatingForBonus}</div>
        </div>
        <div className="sText">
          <div>{`Bonus per delivery :`}</div>
          <div>
            {NumberAsInvoiceString(
              calcBonusCompensationBasedOnTravelMode(
                courierInvoiceForSelectedCourier.courierTravelMode,
                isOutsideOsloSelected
              )
            )}{' '}
          </div>
        </div>
        {isOutsideOsloSelected ? (
          <div className="sText">
            <div>Min comp per shift: </div>
            <div>
              {NumberAsInvoiceString(
                compensationModelStavanger.minTotalCompForShift
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function SummaryInvoice({
  ordersPerDay,
  courierInvoiceForSelectedCourier,
  startDate,
  endDate,
  region,
}: {
  ordersPerDay: PerDayInvoice[];
  courierInvoiceForSelectedCourier: CourierInvoice;
  startDate: Dayjs;
  endDate: Dayjs;
  region: Region;
}): JSX.Element {
  const isOutsideOsloSelected = useOutsideOsloCompensationModel(region);
  const {
    totalNrOforders,
    sumCompensationForShifts,
    SumCompensation,
    nrOfDayShifts,
    compForDeliveries,
    extraWorkCompensation,
    minWageSummary,
    minWageDays,
  } = calcInvoiceSummary(ordersPerDay, isOutsideOsloSelected);
  const travelModeForCourier =
    courierInvoiceForSelectedCourier.courierTravelMode;

  const deviationAvgCompensationToShow = isOutsideOsloSelected
    ? 0
    : calcSumDeviationAvgCompensation(totalNrOforders, travelModeForCourier);

  const { courierName, orgNummer } = getInvoiceSummaryData(
    courierInvoiceForSelectedCourier
  );
  const { isMva, fetchIsMvaStatus, vatForCouriers } = useFetchIsMva(orgNummer);

  return (
    <>
      <InvoiceHeader startDate={startDate} endDate={endDate} />

      <div className="SummaryInvoice invoiceCourier">
        <CompanyInfoInvoiceBox
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          courierName={courierName}
          orgNummer={orgNummer}
          fetchIsMvaStatus={fetchIsMvaStatus}
          isMva={isMva}
        />

        <PeriodCompensationInfoBox
          nrOfDayShifts={nrOfDayShifts}
          sumCompensationForShifts={sumCompensationForShifts}
          totalNrOforders={totalNrOforders}
          compForDeliveries={compForDeliveries}
          SumCompensation={SumCompensation}
          vatForCouriers={vatForCouriers}
          deviationComp={deviationAvgCompensationToShow}
          extraWorkCompensation={extraWorkCompensation}
          courierAvgRatingForDateRange={
            courierInvoiceForSelectedCourier.courierAvgRatingForDateRange
          }
          minWageSummary={minWageSummary}
          minWageDays={minWageDays}
          region={region}
        />

        <CompensationInfoBox
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          region={region}
        />
      </div>
      <div className="positionBoWideItem invoiceTable">
        <InvoiceTable ordersPerDay={ordersPerDay} region={region} />
      </div>
    </>
  );
}

export function InvoiceTable({
  ordersPerDay,
  region,
}: {
  ordersPerDay: PerDayInvoice[];
  region: Region;
}): JSX.Element {
  return (
    <>
      {ordersPerDay.length > 0 && (
        <div className="courierPeriodReport">
          <table className="BillingCourierTable">
            <thead>
              <tr>
                <th>Shift</th>
                <th>Shift comp</th>
                <th>Deliveries </th>
                <th>Deliveries comp</th>
                <th>Total comp</th>
              </tr>
            </thead>

            <tbody>
              {ordersPerDay.map((order: PerDayInvoice, index: number) => {
                return (
                  <ShowCompensationPdfTable
                    order={order}
                    index={index}
                    key={index}
                    region={region}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ShowCompensationPdfTable({
  order,
  index,
  region,
}: {
  order: PerDayInvoice;
  index: number;
  region;
}): JSX.Element {
  const isOutsideOsloSelected = useOutsideOsloCompensationModel(region);

  const listOfExtraWork: ExtraWork[] =
    order.shift !== undefined &&
    order.shift.extraWork !== undefined &&
    order.shift.extraWork.length > 0 &&
    order.shift.extraWork.filter(obj => obj !== undefined);

  let totalCompToShow = roundToTwo(
    order.totalCompensation - order.deviationCompensation
  );
  if (isOutsideOsloSelected && totalCompToShow < 1400 && totalCompToShow > 0) {
    totalCompToShow = compensationModelStavanger.minTotalCompForShift;
  }
  return (
    <>
      <tr key={`${order.date}-${Math.random()}`}>
        <td>{dayjs(order.date).format('DD.MM.YY')}</td>
        <td>{order.compensationForShift} </td>
        <td>{order.nrOfOrders} </td>
        <td>{order.compensationForNormalDeliveries} </td>
        <td>{totalCompToShow}</td>
      </tr>
      {listOfExtraWork.length > 0 &&
        listOfExtraWork.map((obj, index) => {
          return (
            <tr key={`${index}_${obj.typeOfExtraWork}`}>
              <td>{obj.typeOfExtraWork} </td>
              <td>{obj.payment} </td>
            </tr>
          );
        })}
    </>
  );
}

export function InvoiceCourier({
  courierInvoiceForSelectedCourier,
  startDate,
  endDate,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  courierInvoiceForSelectedCourier: CourierInvoice;
  startDate: Dayjs;
  endDate: Dayjs;
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}): JSX.Element {
  const useOutsideOsloCompensation = [
    Region.STAVANGER,
    Region.BERGEN,
    Region.TRONDHEIM,
  ].includes(region);

  const ordersPerDay: PerDayInvoice[] = prodCostTableForCourierWithShiftData(
    courierInvoiceForSelectedCourier,
    courierInvoiceForSelectedCourier
      ? courierInvoiceForSelectedCourier.courierTravelMode
      : CourierTravelModeString.CAR.toString(),
    courierListBookedFetched,
    fetchStatusShift,
    useOutsideOsloCompensation
  );
  if (fetchStatusShift === ReqEnum.SUCCESS && ordersPerDay.length) {
    return (
      <div className="invoiceCourier">
        <SummaryInvoice
          ordersPerDay={ordersPerDay}
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          startDate={startDate}
          endDate={endDate}
          region={region}
        />
      </div>
    );
  } else {
    return (
      <>
        (
        <Loader />
      </>
    );
  }
}

export function BillingCourier({
  courierInvoiceList,
  fetchStatus,
  startDate,
  endDate,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  courierInvoiceList: CourierInvoice;
  fetchStatus: reqStatus;
  startDate: Dayjs;
  endDate: Dayjs;
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}): JSX.Element {
  if (
    courierInvoiceList === undefined &&
    courierListBookedFetched === undefined
  ) {
    return <div style={{ marginLeft: '5px' }}> No Records </div>;
  }

  return (
    <>
      <div
        className={
          (fetchStatus || fetchStatusShift) === ReqEnum.PENDING
            ? 'orderlisttable is-loading'
            : 'orderlisttable'
        }
      >
        <div className="orderlisttable__header">
          <div className="orderlisttable__header__buttons u-screen-only">
            <ButtonComponent
              onClick={() => window.print()}
              disabled={courierInvoiceList === undefined}
            >
              Download PDF
            </ButtonComponent>
          </div>
        </div>

        <InvoiceCourier
          courierInvoiceForSelectedCourier={courierInvoiceList}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      </div>
    </>
  );
}
